import { useState, FC, PropsWithChildren, useEffect } from "react";
import _ from "lodash";
import { useScreen } from "hooks/useScreen";
import ErrorBoundary from "utils/errorBoundary";
import { getItemWidth, pixelToGridUnits } from "utils/gridstack";

const DASHBOARD_MIN_WIDTH = 3;
const OLD_DASHBOARD_WIDTH = 4;
export const CELL_HEIGHT = 10;

const DashboardGridStack: FC<
    PropsWithChildren<
        Partial<{
            id: string;
            idKey: string;
            width: number;
            gsH?: number;
            onSizeChange: any;
            /** Specify minimum width in pixels, use this to ensure the minimum width on small screens or when the users resize the items */
            minWidthPx?: number;
            gridWrapper?: React.RefObject<any | null>;
            [key: string]: any;
        }>
    >
> = (props) => {
    const {
        children,
        id,
        idKey,
        width,
        minWidthPx,
        // gsH = 41,
        height = 410,
        onSizeChange,
        gridWrapper,
        ...rest
    } = props;
    const [minWidth, setMinWidth] = useState<number>(DASHBOARD_MIN_WIDTH);
    const [itemWidth, setItemWidth] = useState<number>(OLD_DASHBOARD_WIDTH);
    const { width: screenWidth } = useScreen();
    const gridWrapperWidth = gridWrapper?.current?.offsetWidth ?? 0;

    const uuid =
        id ??
        _.get(children, (idKey || "props.panel") + ".uuid");
    const panelWidth =
        width ??
        _.get(children, (idKey || "props.panel") + ".size");
    const itemHeight = height / CELL_HEIGHT;

    useEffect(() => {
        // Recalculate min width if minWidthPx is provided. This will convert pixel to grid unit
        if (minWidthPx) {
            if (!gridWrapperWidth) {
                return;
            }
            const minGsW = pixelToGridUnits(minWidthPx, gridWrapperWidth);
            setMinWidth(minGsW);
        } else {
            setMinWidth(getItemWidth(screenWidth));
        }

        const newItemWidth = getItemWidth(
            screenWidth,
            panelWidth || OLD_DASHBOARD_WIDTH
        );

        const actualWidth = newItemWidth < minWidth ? minWidth : newItemWidth;
        setItemWidth(actualWidth);

        if (typeof onSizeChange === "function") {
            onSizeChange(uuid, actualWidth);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screenWidth, panelWidth, uuid, minWidthPx, gridWrapperWidth, minWidth]);

    return (
        <div
            className="grid-stack-item"
            id={uuid}
            key={uuid}
            {...{
                "gs-w": itemWidth,
                "gs-h": itemHeight,
                "gs-min-w": minWidth,
            }}
            data-gs-auto-position="1"
            {...rest}
        >
            <div className="dashboard-grid-stack-item grid-stack-item-content">
                <ErrorBoundary>{children}</ErrorBoundary>
            </div>
        </div>
    );
};

export default DashboardGridStack;
